'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Chatbot from './Chatbot';
import './_chatbot.scss';

/** Is `name` among this browser's cookies? */
function hasCookie(name) {
  if (!name || typeof document === 'undefined') return false;
  return document.cookie.split(';').some((pair) => pair.trim().startsWith(`${name}=`));
}

/**
 * Mounts the assistant on every page. The chat now lives as a persistent
 * full-width input bar pinned to the bottom of the viewport (see <Chatbot>),
 * so there is no separate launcher button — the bar itself is the entry point.
 *
 * Excluded from the full-screen map (/map): the bar is `position: fixed` and sits
 * on top of the map, so the assistant has no place there.
 *
 * `editorsOnly` mirrors the WP setting of the same name: the assistant is then
 * shown only in browsers signed in to the CMS, so it can be tried on the live
 * site before visitors meet it. The sign-in marker is a cookie, which only the
 * browser can read, so nothing renders until hydration — which is also what
 * keeps a visitor from seeing the bar flash past. The chat route applies the
 * same rule (see services/editor-access.js).
 */
export default function ChatbotLauncher({
  lang = 'no',
  slug = 'default',
  greeting = '',
  disclaimer = '',
  title = '',
  placeholder = '',
  dictionary = {},
  editorsOnly = false,
  editorCookieName = ''
}) {
  const pathname = usePathname();
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    if (!editorsOnly) return;
    setSignedIn(hasCookie(editorCookieName));
  }, [editorsOnly, editorCookieName]);

  if (pathname === '/map' || pathname?.startsWith('/map/')) return null;
  // No cookie name configured means the limit cannot be applied; showing the
  // assistant beats hiding it from the editors who asked for it.
  if (editorsOnly && editorCookieName && !signedIn) return null;

  return (
    <Chatbot
      lang={lang}
      slug={slug}
      greeting={greeting}
      disclaimer={disclaimer}
      title={title}
      placeholder={placeholder}
      dictionary={dictionary}
    />
  );
}
